import { EntityType } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { DocType, useBlobRepository, useDocumentRepository } from '@frontend/repository';
import { IUser } from '@frontend/user/types';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import otherCourierIcon from '../../../../assets/images/select-courier-other.png';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { SelectCourierOtherModal } from './select-courier-other-modal.component';
import useSelectCourier from './select-courier.controller';

const ID = 'select-courier';

const SelectCourier = () => {
    const viewProps = useSelectCourier();

    return (
        <WizardScreenContainer
            id={ID}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitDisabled: !viewProps.canContinue,
                submitLabel: CommonMessage.BUTTONS.CONTINUE
            }}
            containerClassName='relative flex h-full w-full flex-col content-stretch items-start gap-8 p-6'>
            <p className='text-foreground-default relative w-full shrink-0 text-base leading-normal font-medium'>
                <FormattedMessage
                    id='Wizard.SelectCourier.Instructions'
                    defaultMessage='Drop-off by'
                    description='Heading above the courier logos on the select-courier drop-off screen'
                />
            </p>

            {viewProps.isLoading && !viewProps.showOtherModal ? (
                <Spinner />
            ) : (
                <div className='grid w-full grid-cols-3 gap-3'>
                    {viewProps.couriers.map((courier) => (
                        <button
                            key={courier.id}
                            type='button'
                            onClick={() => viewProps.onCourierClick(courier)}
                            className={`flex aspect-square items-center justify-center rounded-xl border border-solid p-3 transition-colors ${
                                !viewProps.isOtherSelected && viewProps.selectedCourier?.id === courier.id
                                    ? 'bg-brand-light border-brand-secondary'
                                    : 'bg-background-surface border-border-default'
                            }`}>
                            <CourierImage courier={courier} />
                        </button>
                    ))}
                    <button
                        type='button'
                        onClick={viewProps.onOtherClick}
                        className={`flex aspect-square flex-col items-center justify-center gap-2 rounded-xl border border-solid p-3 transition-colors ${
                            viewProps.isOtherSelected ? 'bg-brand-light border-brand-secondary' : 'bg-background-surface border-border-default'
                        }`}>
                        <img
                            alt=''
                            aria-hidden
                            className='max-h-[55%] max-w-full object-contain'
                            src={otherCourierIcon}
                        />
                        <span className='text-always-black px-1 text-center text-sm leading-normal font-medium'>
                            <FormattedMessage
                                id='kiosk.wizard.select-courier.other-tile.name-not-found'
                                defaultMessage='Name not found'
                                description='Visible label on the Other courier tile under the name-not-found icon'
                            />
                        </span>
                    </button>
                </div>
            )}

            <SelectCourierOtherModal
                show={viewProps.showOtherModal}
                value={viewProps.otherName}
                isSubmitting={!!viewProps.isLoading}
                onChange={viewProps.setOtherName}
                onClose={viewProps.closeOtherModal}
                onSubmit={viewProps.onOtherModalSubmit}
            />
        </WizardScreenContainer>
    );
};

export default SelectCourier;

const CourierImage = ({ courier }: { courier: IUser }): React.ReactNode => {
    const documentRepository = useDocumentRepository();
    const blobRepository = useBlobRepository();
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        /**
         * @deprecated Legacy document-based image fallback.
         * Keep for backwards compatibility when no attachment blob is available.
         */
        const fetchLegacyDocumentImage = async () => {
            const service = await documentRepository.getFiles(EntityType.USER, courier.id);
            const image = service.getDocuments().find((d) => service.getType(d.id) === DocType.IMAGE);
            if (!image) return null;
            return await service.getUrl(image.id);
        };

        const fetchImage = async () => {
            try {
                setIsLoading(true);

                try {
                    const blobInfo = await blobRepository.getBlob(courier.account_id, EntityType.USER, courier.id);
                    if (blobInfo?.url) {
                        setImageUrl(blobInfo.url);
                        return;
                    }
                } catch {
                    // Fallback to legacy document flow when new attachment image is unavailable.
                }

                const fallbackUrl = await fetchLegacyDocumentImage();
                setImageUrl(fallbackUrl ?? null);
            } catch (error) {
                Logger.error('Failed to fetch user image:', error);
                setImageUrl(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchImage();
    }, [courier.account_id, courier.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (isLoading) {
        return (
            <div className='relative flex max-h-full max-w-full items-center justify-center'>
                <Spinner />
            </div>
        );
    }

    if (imageUrl) {
        return (
            <div className='relative flex max-h-full max-w-full items-center justify-center'>
                <img
                    alt={`${courier.first_name} ${courier.last_name}`}
                    className='max-h-full max-w-full object-contain'
                    src={imageUrl}
                />
            </div>
        );
    }

    return (
        <div className='relative flex max-h-full max-w-full items-center justify-center'>
            <div className='text-always-black flex flex-col justify-center px-2 text-center text-sm leading-normal font-medium break-words whitespace-pre-wrap'>
                <p className='mb-0'>{courier.first_name}</p>
                <p className='mb-0'>{courier.last_name}</p>
            </div>
        </div>
    );
};
